import type { DateOption } from 'flatpickr/dist/types/options';
import { getIn, useFormikContext } from 'formik';
import { useCallback } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { Label } from '@hivebrite/design-system/Label';
import { useTheme } from '@hivebrite/design-system/Theme';
import { getDateLabelText } from '@hivebrite/utils';

import { useLocale } from 'hb-react/shared/hooks/useLocale';
import dayjs from 'hb-react/shared/utils/dayjs/dayjs';

import DateInput from '../Date';

interface DateRangeProps {
  disabled?: boolean;
  disableFrom?: boolean;
  disableTo?: boolean;
  displayFormat?: string;
  enableTime?: boolean;
  icon?: React.FC;
  id?: string;
  label?: string;
  labelMultiline?: boolean;
  locale?: string;
  maxDate?: DateOption;
  maxName: string;
  maxLabel?: string;
  minDate?: DateOption;
  minName: string;
  minLabel?: string;
  placeholder?: {
    max?: string;
    min?: string;
  };
  tooltip?: string;
  validate?: {
    required?: boolean;
  };
  wrapperProps?: object;
  preserveDuration?: boolean;
  disableStartMinDate?: boolean;
}

const DateRange = ({
  disabled,
  disableFrom,
  disableTo,
  displayFormat,
  enableTime,
  id,
  label,
  labelMultiline,
  maxName,
  maxLabel,
  minName,
  minLabel,
  placeholder,
  tooltip,
  locale: initialLocale,
  icon,
  maxDate,
  minDate,
  validate,
  wrapperProps,
  preserveDuration,
  disableStartMinDate = false,
}: DateRangeProps) => {
  const [locale] = useLocale();
  const theme = useTheme();
  const htmlId = id || minName;
  const { values, setFieldValue } = useFormikContext();
  const minValue = getIn(values, minName);
  const maxValue = getIn(values, maxName);
  const labelText = getDateLabelText(label);
  const minId = `${htmlId}-min`;
  const maxId = `${htmlId}-max`;
  const required = validate?.required;

  const onStartDateChange = useCallback(
    (newStartDate: string) => {
      const newDate = dayjs(newStartDate);

      if (!preserveDuration || !newDate.isValid()) {
        return;
      }

      if (minValue && maxValue) {
        const prevStart = dayjs(minValue);
        const prevEnd = dayjs(maxValue);

        if (prevStart.isValid() && prevEnd.isValid()) {
          const duration = prevEnd.diff(prevStart);
          const newEnd = newDate.add(duration, 'ms');

          setFieldValue(maxName, newEnd.toISOString());
        }
      }
    },
    [preserveDuration, minValue, maxValue, setFieldValue, maxName],
  );

  return (
    <Box mb={6} display="block" {...wrapperProps}>
      {labelText && (
        <Label
          id={minId}
          disabled={disabled}
          multiline={labelMultiline}
          required={required}
          wrapperProps={{
            opacity: disabled ? theme.input.disabled.opacity : 1,
          }}
        >
          {labelText}
        </Label>
      )}
      <Box>
        <Box flex={1} display="block" mr={3}>
          <DateInput
            disabled={disabled || disableFrom}
            displayFormat={displayFormat}
            enableTime={enableTime}
            hideError
            icon={icon}
            id={minId}
            locale={initialLocale || locale}
            maxDate={disableStartMinDate ? undefined : maxValue || maxDate}
            minDate={disableStartMinDate ? undefined : minDate}
            name={minName}
            onChange={onStartDateChange}
            placeholder={placeholder?.min}
            tooltip={tooltip}
            validate={{ required }}
            wrapperProps={{ mb: 0 }}
            label={minLabel}
          />
        </Box>
        <Box flex={1} display="block" ml={3}>
          <DateInput
            ariaLabel={__('Max value for %{label}').i__({ label: label || '' })}
            disabled={disabled || disableTo}
            displayFormat={displayFormat}
            enableTime={enableTime}
            icon={icon}
            id={maxId}
            locale={locale}
            maxDate={maxDate}
            minDate={minValue || minDate}
            name={maxName}
            placeholder={placeholder?.max}
            tooltip={tooltip}
            validate={{ required }}
            wrapperProps={{ mb: 0 }}
            label={maxLabel}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default DateRange;
